import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Create the context
const VideoContext = createContext(null);

// Hook to use the video context
export const useVideo = () => {
  const context = useContext(VideoContext);
  if (!context) {
    throw new Error('useVideo must be used within VideoProvider');
  }
  return context;
};

// Provider component that wraps the app
export const VideoProvider = ({ children }) => {
  // Connection state
  const [roomCode, setRoomCode] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [connected, setConnected] = useState(false);
  const [peerConnected, setPeerConnected] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [micEnabled, setMicEnabled] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [status, setStatus] = useState('Not connected');
  
  // Refs for WebRTC
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  
  const log = useCallback((msg) => {
    console.log(`[VideoContext] ${msg}`);
  }, []);

  // Join a video room
  const joinRoom = useCallback(async (code, asHost = false) => {
    if (connected && roomCode === code) {
      log('Already connected to this room');
      return true;
    }
    
    // Disconnect from any existing room first
    if (connected) {
      await leaveRoom();
    }
    
    try {
      log(`Joining room ${code} as ${asHost ? 'HOST' : 'CLIENT'}`);
      setStatus('Getting camera...');
      setRoomCode(code);
      setIsHost(asHost);
      
      // Get media
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true
      });
      
      // Mute mic by default to prevent feedback
      stream.getAudioTracks().forEach(t => t.enabled = false);
      setMicEnabled(false);
      
      localStreamRef.current = stream;
      setLocalStream(stream);
      
      log('Camera ready, creating peer connection...');
      setStatus('Setting up connection...');
      
      // Create peer connection with STUN and TURN servers for better connectivity
      const pc = new RTCPeerConnection({
        iceServers: [
          // Google STUN servers
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' },
          // Free TURN servers for NAT traversal backup
          {
            urls: 'turn:openrelay.metered.ca:80',
            username: 'openrelayproject',
            credential: 'openrelayproject'
          },
          {
            urls: 'turn:openrelay.metered.ca:443',
            username: 'openrelayproject',
            credential: 'openrelayproject'
          },
          {
            urls: 'turn:openrelay.metered.ca:443?transport=tcp',
            username: 'openrelayproject',
            credential: 'openrelayproject'
          }
        ],
        iceTransportPolicy: 'all',
        iceCandidatePoolSize: 10
      });
      pcRef.current = pc;
      
      // Add local tracks
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });
      
      // Handle remote tracks
      pc.ontrack = (event) => {
        log('Received remote track: ' + event.track.kind);
        if (event.streams[0]) {
          setRemoteStream(event.streams[0]);
          setPeerConnected(true);
          setStatus('Connected!');
          toast.success('Video connected!');
        }
      };
      
      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'ice-candidate',
            candidate: event.candidate
          }));
        }
      };
      
      // ICE connection state - only restart on actual failure, not on brief disconnects
      let iceRestartTimeout = null;
      let lastRestartTime = 0;
      const MIN_RESTART_INTERVAL = 10000; // Don't restart more than once every 10 seconds
      
      pc.oniceconnectionstatechange = () => {
        log('ICE state: ' + pc.iceConnectionState);
        
        // Clear any pending restart
        if (iceRestartTimeout) {
          clearTimeout(iceRestartTimeout);
          iceRestartTimeout = null;
        }
        
        if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
          setPeerConnected(true);
          setStatus('Connected!');
        } else if (pc.iceConnectionState === 'disconnected') {
          // Brief disconnects are normal - wait before showing status change
          setStatus('Connection unstable...');
          
          // Only try restart after 5 seconds if still disconnected
          iceRestartTimeout = setTimeout(() => {
            const now = Date.now();
            if (pc.iceConnectionState === 'disconnected' && 
                wsRef.current?.readyState === WebSocket.OPEN &&
                now - lastRestartTime > MIN_RESTART_INTERVAL) {
              log('Attempting ICE restart after prolonged disconnect...');
              lastRestartTime = now;
              pc.restartIce();
            }
          }, 5000);
        } else if (pc.iceConnectionState === 'failed') {
          setPeerConnected(false);
          setStatus('Connection failed');
          
          // Only restart if enough time has passed
          const now = Date.now();
          if (now - lastRestartTime > MIN_RESTART_INTERVAL) {
            log('ICE failed, attempting restart...');
            lastRestartTime = now;
            setTimeout(() => {
              if (pc.iceConnectionState === 'failed') {
                pc.restartIce();
              }
            }, 2000);
          }
        }
      };
      
      // Connect WebSocket
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = API.replace(/^https?:\/\//, '');
      const role = asHost ? 'host' : 'client';
      const wsUrl = `${wsProtocol}//${wsHost}/api/video-room/ws/${code}/${role}`;
      
      log('Connecting WebSocket: ' + wsUrl);
      setStatus('Connecting to server...');
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        log('WebSocket connected');
        setConnected(true);
        setStatus(asHost ? 'Waiting for client...' : 'Waiting for host...');
        toast.success(`Connected to room ${code}`);
        
        // Keep-alive ping every 15 seconds to prevent timeout
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          } else {
            clearInterval(pingInterval);
          }
        }, 15000);
        
        // Store interval to clear on close
        ws._pingInterval = pingInterval;
        
        // HOST sends offer shortly after connecting
        if (asHost) {
          setTimeout(async () => {
            if (pcRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
              try {
                log('Creating initial offer...');
                const offer = await pcRef.current.createOffer({
                  offerToReceiveAudio: true,
                  offerToReceiveVideo: true
                });
                await pcRef.current.setLocalDescription(offer);
                wsRef.current.send(JSON.stringify({ type: 'offer', sdp: offer.sdp }));
                log('Sent initial offer');
              } catch (err) {
                log('Initial offer error: ' + err.message);
              }
            }
          }, 500);
        }
      };
      
      ws.onmessage = async (event) => {
        const msg = JSON.parse(event.data);
        log('WS message: ' + msg.type);
        
        const currentPc = pcRef.current;
        if (!currentPc) return;
        
        try {
          if (msg.type === 'peer_joined') {
            log('Peer joined!');
            setStatus('Peer joined, connecting...');
            
            // HOST creates offer
            if (asHost) {
              const offer = await currentPc.createOffer({
                offerToReceiveAudio: true,
                offerToReceiveVideo: true
              });
              await currentPc.setLocalDescription(offer);
              ws.send(JSON.stringify({ type: 'offer', sdp: offer.sdp }));
              log('Sent offer');
            }
          }
          
          if (msg.type === 'offer' && !asHost) {
            log('Received offer');
            await currentPc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: msg.sdp }));
            const answer = await currentPc.createAnswer();
            await currentPc.setLocalDescription(answer);
            ws.send(JSON.stringify({ type: 'answer', sdp: answer.sdp }));
            log('Sent answer');
          }
          
          if (msg.type === 'answer' && asHost) {
            log('Received answer');
            await currentPc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: msg.sdp }));
          }
          
          if (msg.type === 'ice-candidate' && msg.candidate) {
            await currentPc.addIceCandidate(new RTCIceCandidate(msg.candidate));
          }
          
          if (msg.type === 'peer_left') {
            log('Peer left');
            setPeerConnected(false);
            setRemoteStream(null);
            setStatus(asHost ? 'Client disconnected - waiting...' : 'Host disconnected - waiting...');
          }
        } catch (err) {
          log('Message handling error: ' + err.message);
        }
      };
      
      ws.onerror = () => {
        log('WebSocket error');
        setStatus('Connection error');
      };
      
      ws.onclose = (event) => {
        log('WebSocket closed: ' + event.code);
        // Clear ping interval
        if (ws._pingInterval) {
          clearInterval(ws._pingInterval);
        }
        setConnected(false);
        
        // Auto-reconnect WebSocket if it wasn't a clean close
        // Code 1000 = normal, 1001 = going away (page close)
        if (event.code !== 1000 && event.code !== 1001) {
          setStatus('Reconnecting WebSocket...');
          log('Unexpected close, will reconnect in 2 seconds...');
          
          setTimeout(() => {
            // Only reconnect if we still have the peer connection and stream
            if (pcRef.current && localStreamRef.current) {
              log('Reconnecting WebSocket...');
              
              const newWsUrl = `${wsProtocol}//${wsHost}/api/video-room/ws/${code}/${role}`;
              const newWs = new WebSocket(newWsUrl);
              wsRef.current = newWs;
              
              newWs.onopen = () => {
                log('WebSocket reconnected!');
                setConnected(true);
                setStatus('Reconnected!');
                toast.success('Connection restored!');
                
                // Start ping interval for new connection
                const newPingInterval = setInterval(() => {
                  if (newWs.readyState === WebSocket.OPEN) {
                    newWs.send(JSON.stringify({ type: 'ping' }));
                  } else {
                    clearInterval(newPingInterval);
                  }
                }, 15000);
                newWs._pingInterval = newPingInterval;
              };
              
              // Copy the same message handler
              newWs.onmessage = ws.onmessage;
              newWs.onerror = ws.onerror;
              newWs.onclose = ws.onclose;
            }
          }, 2000);
        } else {
          setStatus('Disconnected');
        }
      };
      
      return true;
    } catch (err) {
      log('Join error: ' + err.message);
      setStatus('Failed: ' + err.message);
      toast.error('Failed to join: ' + err.message);
      return false;
    }
  }, [connected, roomCode, log]);

  // Leave the current room
  const leaveRoom = useCallback(async () => {
    log('Leaving room');
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setRoomCode(null);
    setIsHost(false);
    setConnected(false);
    setPeerConnected(false);
    setLocalStream(null);
    setRemoteStream(null);
    setStatus('Not connected');
  }, [log]);

  // Toggle microphone
  const toggleMic = useCallback(() => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setMicEnabled(track.enabled);
      }
    }
  }, []);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getVideoTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setVideoEnabled(track.enabled);
      }
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(t => t.stop());
      }
      if (pcRef.current) {
        pcRef.current.close();
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const value = {
    // State
    roomCode,
    isHost,
    connected,
    peerConnected,
    localStream,
    remoteStream,
    micEnabled,
    videoEnabled,
    status,
    
    // Actions
    joinRoom,
    leaveRoom,
    toggleMic,
    toggleVideo,
  };

  return (
    <VideoContext.Provider value={value}>
      {children}
    </VideoContext.Provider>
  );
};

export default VideoContext;
